import { Button, Input, Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, TriangleAlertIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Address, ContractFunctionRevertedError } from "viem";
import { proposeToken } from "../../actions/proposeToken";
import { useIsValidEthTokenAddr } from "../../queries/ethereum";
import { allTokensQueryKey } from "../../queries/tokenReg";
import { storeTokenRegTxHash } from "../../state/tokenRegTxHash";
import { EthTokenDetails } from "./EthTokenDetails";

export function ProposeTokenModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const queryClient = useQueryClient();

  const [tokenAddress, setTokenAddress] = useState("");

  const isValid = useIsValidEthTokenAddr(tokenAddress as `0x${string}`);

  const { mutate, reset, isPending, error } = useMutation({
    mutationFn: async () => {
      return proposeToken(tokenAddress);
    },
    onSuccess: (data) => {
      storeTokenRegTxHash(tokenAddress as Address, {
        proposedTxHash: data,
      });

      // Invalidate the token list since a new token was proposed
      queryClient.invalidateQueries({ queryKey: allTokensQueryKey() });
      queryClient.invalidateQueries({ queryKey: ["token_reg_tx_hash"] });

      onSuccess();
    },
  });

  // Reset the state when the modal is closed
  useEffect(() => {
    if (isOpen) return;
    setTokenAddress("");
    reset();
  }, [isOpen, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top"
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      classNames={{
        base: "turnstile-modal-border rounded-3xl",
        closeButton: "text-white text-xl top-4 right-5",
        header: "pt-5 pb-3",
        body: "items-center p-6 pt-0 gap-4",
        backdrop: "turnstile-modal-backdrop",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col">
          <div className="font-semibold">Propose Token</div>
          <div className="font-[400] text-[#ADC0D0] text-sm mr-4">
            By proposing a token, you are sending a request to allow it to be added to the network. The entire approval
            process can take up to 3 days.
          </div>
        </ModalHeader>

        <ModalBody className="mt-4 gap-6">
          <Input
            label="Token Address"
            variant="flat"
            value={tokenAddress}
            onValueChange={(value) => {
              if (error) {
                reset();
              }
              setTokenAddress(value);
            }}
            endContent={isValid && <CheckIcon className="text-success" />}
            classNames={{
              inputWrapper:
                "bg-[var(--turnstile-input-color)] group-data-[hover=true]:bg-[var(--turnstile-input-hover-color)] group-data-[focus=true]:bg-[var(--turnstile-input-color)]",
            }}
          />

          {error && (
            <div className="text-sm flex items-center gap-1">
              <TriangleAlertIcon className="text-danger" />
              {(error as ContractFunctionRevertedError).shortMessage}
            </div>
          )}

          {tokenAddress && isValid && <EthTokenDetails tokenAddress={tokenAddress as Address} />}

          <Button
            fullWidth
            size="lg"
            color={isValid ? "primary" : "default"}
            radius="full"
            isDisabled={!isValid}
            isLoading={isPending}
            onPress={() => mutate()}
            className={isValid ? "" : "bg-[var(--turnstile-input-color)]"}
          >
            Propose
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
